import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, input, model } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideMapPin, lucideUsers } from '@ng-icons/lucide';
import { effectInputChanges } from '../utils/effect-input-changes';
import { ISvzUserAvatar, UserAvatarSize } from './interfaces/svz-user-avatar.interface';
import { normalizeUserAvatarSize } from './svz-user-avatar-helpers';

@Component({
  selector: 'svz-user-avatar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-user-avatar.component.html',
  viewProviders: [provideIcons({ lucideMapPin, lucideUsers })],
  host: {
    class: 'inline-flex',
  },
  standalone: true,
  imports: [NgTemplateOutlet, NgIcon],
})
export class SvzUserAvatarComponent implements OnInit {
  readonly info = model<ISvzUserAvatar | null>(null);

  readonly borderColor = input<string>('white', { alias: 'border-color' });

  readonly highDefinition = input(false);

  protected fontSize = 0;
  protected size = 0;
  protected initials = '';
  protected readonly userAvatarSize = UserAvatarSize;
  protected isHundredPercentSize = false;

  private wasInitialized = false;

  ngOnInit(): void {
    const info = this.info() ?? {
      name: '',
      size: UserAvatarSize.Header,
    };

    const names =
      info.name
        ?.replace(/\([^)]*\)|[^a-zA-Z 0-9]/g, '')
        .trim()
        .split(' ')
        .filter(Boolean) ?? [];
    const firstName = names.length ? names[0].substring(0, 1) : '';
    const lastName = names.length > 1 ? names[names.length - 1].substring(0, 1) : '';

    this.initials = `${firstName}${lastName}`.toUpperCase();
    this.size = normalizeUserAvatarSize(info.size);
    this.fontSize = info.initialsFontSize ?? this.size * 0.5;
    this.isHundredPercentSize = (info.size ?? 0) < 0;
    this.wasInitialized = true;
  }

  private readonly syncInputChanges = effectInputChanges(
    {
      info: () => this.info(),
      borderColor: () => this.borderColor(),
      highDefinition: () => this.highDefinition(),
    },
    (changes) => {
      if (this.wasInitialized) {
        this.ngOnInit();
      }
    }
  );

  protected get resolvedImageSrc(): string | null {
    const info = this.info();
    if (!info) {
      return null;
    }

    return (this.highDefinition() && info.pictureHQ) || info.picture || null;
  }

  /** `#fff` fallback matches the previous hardcoded `text-white` for every existing consumer. */
  protected get resolvedInitialsTextColor(): string {
    return this.info()?.initialsTextColor || '#fff';
  }

  protected get showBorder(): boolean {
    const override = this.info()?.showBorder;
    if (override !== undefined) {
      return override;
    }

    return !this.isHundredPercentSize && this.info()?.size !== UserAvatarSize.CustomLayout;
  }

  protected get resolvedBorderColor(): string {
    const borderColor = this.borderColor();
    switch (borderColor) {
      case 'primary':
        return 'var(--svc-color-primary)';
      case 'on-primary':
        return 'var(--svc-color-on-primary)';
      case 'accent':
        return 'var(--svc-accent)';
      case 'warn':
      case 'danger':
        return 'var(--svc-warn)';
      case 'border':
        return 'var(--svc-border)';
      default:
        return borderColor;
    }
  }

  protected onImgLoadError(): void {
    const info = this.info();
    if (!info) {
      return;
    }

    const nextInfo = { ...info };
    if (this.highDefinition() && info.pictureHQ) {
      nextInfo.pictureHQ = null;
      if (info.pictureHQ === info.picture) {
        nextInfo.picture = null;
      }
    } else {
      nextInfo.picture = null;
    }

    this.info.set(nextInfo);
    this.ngOnInit();
  }
}
